import { observable, computed, action, makeObservable } from 'mobx'
import React from 'react'
import axios from 'axios'
import { selectionTypes } from 'constants.js'
import _ from 'lodash'
import api from 'api.js'
import { uiStore, dataStore, rightSidebarStore } from './UIStore'
import errorStore from './ErrorStore'
import Geopolygon from './Geopolygon'
import TargetGroup from './TargetGroup'
import SavedMarker from './SavedMarker'
import Boundary from './Boundary'
import ColorIcon from '../components/common/icons/ColorIcon'
import ColorPane from '../components/tools/panes/ColorPane'

// Represents the currently-selected item. For example:
// * When the user has selected an object like a target group or marker,
//   `item` is the object, and `type` is the entry in `selectionTypes`
//   describing the object.
// * When the user has selected "Add Item", which does not have an associated object,
//  `item` will be null, and `type` will be selectionTypes.NEW_ITEM
// * When nothing is selected, `item` will be null, and `type` will be selectionTypes.NONE
//
// `data` stores optional extra information, such as:
// * the target group a new item will be added to
// * a `focusedInteraction` boolean, which if `true` means the UI will not allow
//   the selection to change until OK or Cancel is clicked. This is used for shape
//   drawing to avoid a user losing their shape if they mistakenly click
//   an item on the tool bar.
// * any other property which is useful to describe the selection
//
class SelectionStore {
  @observable _item
  @observable _type
  @observable _data
  @observable _editableShape
  @observable _editControl
  @observable _isEditing = false
  @observable _menuItem = null
  @observable _showSubTools = false
  @observable _selectedTargetGroup = null
  @observable _selectedColor = null
  @observable _selectedTransparency = 50
  @observable _selectedMenuItems = []
  @observable _selectedItemStatuses = []
  @observable selectedStatus = null
  @observable selectedTags = []
  // for shape-drag functionality
  // @observable _isMoving = false

  constructor () {
    makeObservable(this)
  }

  @computed get item () { return this._item }
  @computed get type () { return this._type }
  @computed get data () { return this._data }
  @computed get editableShape () { return this._editableShape }
  @computed get editControl () { return this._editControl }
  @computed get isEditing () { return this._isEditing }
  @computed get menuItem () { return this._menuItem }
  @computed get showSubTools () { return this._showSubTools }
  @computed get selectedTargetGroup () { return this._selectedTargetGroup }
  @computed get selectedColor () { return this._selectedColor }
  @computed get selectedTransparency () { return this._selectedTransparency }
  @computed get selectedMenuItems () { return this._selectedMenuItems }
  @computed get selectedItemStatuses () {
    return this.type === selectionTypes.TARGET_GROUP
      ? dataStore.folderStatuses
      : dataStore.shapeStatuses
  }

  @computed get statusOptions () {
    const statuses = this.selectedItemStatuses
    if (Array.isArray(statuses) && statuses.length > 0) {
      return statuses.map(status => ({
        id: status.id,
        value: status.name,
        label: status.name,
        icon: React.createElement(ColorIcon, { fill: status.color, size: 12, outline: status.color }),
        showIcon: false
      }))
    }
    return [{
      id: -999,
      value: '',
      label: 'Please configure statuses in Edit → Status.',
      selected: true,
      icon: null,
      isDisabled: true
    }]
  }
  // for shape-drag functionality
  // @computed get isMoving () { return this._isMoving }

  @action getNewCancelSource () {
    if (this.cancelSource) {
      this.cancelSource.cancel('Cancelled due to new selection')
    }
    this.cancelSource = axios.CancelToken.source()
    // Also immediately cancel any in-flight RightSidebarStore fetches
    // so previous data never overwrites the newly-selected item's UI.
    if (rightSidebarStore && rightSidebarStore.getNewCancelSource) {
      rightSidebarStore.getNewCancelSource()
    }
    return this.cancelSource
  }

  set selectedTargetGroup (value) {
    this._selectedTargetGroup = value
  }

  set selectedColor (value) {
    this._selectedColor = value
  }

  set selectedTransparency (value) {
    this._selectedTransparency = value
  }

  set selectedMenuItems (value) {
    this._selectedMenuItems = value
  }

  @action set ({ item, type, data }) {
    if (
      !this._item ||
      !this._item.deselect ||
      this._item.deselect()
    ) {
      this._item = item
      this._type = type
      this._data = data || {}
    }
  }

  // items which implement a `selection` property can be selected
  // with selectionStore.select(item)
  _resolveSelection (item, overrides) {
    let selection
    if (item && item.pk && !item.type) {
      selection = { item, type: selectionTypes.TARGET_GROUP }
    } else if (item && item.is_general !== undefined) {
      selection = { item, type: selectionTypes.SAVED_MARKER }
    } else if (item && item.layer === 'pointBuffer') {
      selection = { item, type: selectionTypes.POINT_BUFFER }
    } else if (item && item.layer === 'isochrone') {
      selection = { item, type: selectionTypes.ISOCHRONE }
    } else if (item instanceof Geopolygon || (item && item.geometry && item.geometry.type === 'MultiPolygon')) {
      selection = { item, type: selectionTypes.GEOPOLYGON }
    } else if (item instanceof Boundary || (item && item.code)) {
      selection = { item, type: selectionTypes.BOUNDARY }
    } else if (item && item.type === 'FeatureCollection') {
      selection = { item, type: selectionTypes.BOUNDARY_LIST }
    } else if (item && item.layer === 'shape') {
      selection = { item, type: selectionTypes.SHAPE }
    } else if (item && item.layer === 'line') {
      selection = { item, type: selectionTypes.LINE }
    } else if (item && item.layer === 'marker') {
      selection = { item, type: selectionTypes.MARKER }
    } else if (item && item.type) {
      selection = item
    } else {
      selection = item ? item.selection : undefined
    }

    if (selection && selection.type === selectionTypes.TARGET_GROUP && selection.item && selection.item.pk != null) {
      const realGroup = dataStore.targetGroups.find(g => g.pk === selection.item.pk)
      if (realGroup) {
        selection.item = realGroup
      }
    }

    if (!selection) return undefined

    const result = { ...selection, ...overrides }
    if (selection.data && overrides && overrides.data) {
      result.data = { ...selection.data, ...overrides.data }
    }
    return result
  }

  @action selectStatus (value) {
    this.selectedStatus = value
    if (this.item && this.item.updateStatus) {
      this.item.updateStatus(value)
    }
  }

  @action addTag (value) {
    this.selectedTags.push(value.id)
    if (this.item && this.item.updateTags) {
      this.item.updateTags(value.id)
    }
  }

  @action select (item, overrides, isEditing, dontLoadData) {
    if (!isEditing) {
      this.resetAllInteractions()
    }
    uiStore.showIncomeTooltip = false

    // POI Confirm Save re-selects the folder to refresh list state. That
    // should not yank the dashboard off the POI tab onto People. Likewise, keep
    // the Info panel open when switching selection while it's the active tab.
    const skipPeopleTab = !!(overrides && overrides.skipPeopleTab) || uiStore.activeTab === 'info'
    if (overrides && Object.prototype.hasOwnProperty.call(overrides, 'skipPeopleTab')) {
      const rest = { ...overrides }
      delete rest.skipPeopleTab
      overrides = Object.keys(rest).length ? rest : undefined
    }

    if (item && item.name) {
      rightSidebarStore.targetGroupName = item.name
    }

    // const selection = this._resolveSelection(item, overrides)
    const selection = item ? item.selection : undefined

    // Same reasoning for the Copy/Move geometry click-tooltip: it's opened
    // from a button inside the current item's own pane (SubTools or the

    if (uiStore.showClickTooltip && (!selection || selection.item !== this.item)) {
      uiStore.hideClickTooltip()
    }

    // Same reasoning for the delete-confirmation prompt.
    if (uiStore.showDeletePrompt && (!selection || selection.item !== this.item)) {
      uiStore.hideDeletePrompt()
    }

    if (!isEditing) {
      this.disableEdit()
      this.unselect()
      if (!this.hasMultipleSelectedMenuItems) {
        this._selectedMenuItems = item ? [item] : []
      }
    }

    const isDrawingOrEditing = isEditing ||
      (overrides && overrides.data && overrides.data.focusedInteraction) ||
      (selection && selection.data && selection.data.focusedInteraction)

    if (selection && (selection.type === selectionTypes.GEOPOLYGON || selection.type === selectionTypes.POINT_BUFFER || selection.type === selectionTypes.LINE || selection.type === selectionTypes.BOUNDARY || selection.type === selectionTypes.ISOCHRONE || selection.type === selectionTypes.ZIP_CODE_BOUNDARY)) {
      if (!isDrawingOrEditing) {
        uiStore.isPOIMenuExpanded = true
        if (!skipPeopleTab) {
          uiStore.setActiveTab('people')
        }
        rightSidebarStore.setPopulationDataset('landscan')
      }
      if (selection.type === selectionTypes.BOUNDARY || selection.type === selectionTypes.ZIP_CODE_BOUNDARY) {
        this._editableShape = item
        if (!dontLoadData && !isEditing && item.loadAnalysis) {
          uiStore.setProcessing(true)
          item.loadAnalysis(this.cancelSource.token)
        }
      } else {
        this._editableShape = item
        if (selection.type === selectionTypes.GEOPOLYGON && !isEditing && !dontLoadData) {
          uiStore.setProcessing(true)
          console.error('CLICKING ON SHAPE')
          item.loadGeopolygonData(true, this.cancelSource.token)
        }
      }
    }
    const resolveTargetGroup = (g) => {
      if (!g || !g.pk || !dataStore) return g
      return (dataStore.targetGroups && dataStore.targetGroups.find(real => String(real.pk) === String(g.pk))) ||
        (dataStore.sharedTargetGroups && dataStore.sharedTargetGroups.find(real => String(real.pk) === String(g.pk))) ||
        g
    }

    if (selection && selection.type === selectionTypes.TARGET_GROUP) {
      this._selectedItemStatuses = dataStore.folderStatuses
    } else {
      this._selectedItemStatuses = dataStore.shapeStatuses
    }

    if (selection) {
      if (selection.type === selectionTypes.TARGET_GROUP) {
        uiStore.isPOIMenuExpanded = true
        if (!skipPeopleTab) {
          uiStore.setActiveTab('people')
        }
        rightSidebarStore.setPopulationDataset('landscan')
        const realGroup = resolveTargetGroup(selection.item)
        this.selectedTargetGroup = { type: selectionTypes.TARGET_GROUP, item: realGroup }
      } else if (overrides && overrides.data && overrides.data.group) {
        const realGroup = resolveTargetGroup(overrides.data.group)
        this.selectedTargetGroup = {
          type: selectionTypes.TARGET_GROUP,
          item: realGroup
        }
      } else if (selection.item && selection.item.targetGroupId) {
        if (!isDrawingOrEditing) {
          uiStore.isPOIMenuExpanded = true
          if (!skipPeopleTab) {
            uiStore.setActiveTab('people')
          }
          rightSidebarStore.setPopulationDataset('landscan')
        }
        // If we selected a shape, find its parent target group
        const group = dataStore.targetGroups.find(g => g.pk === selection.item.targetGroupId)
        if (group) {
          this.selectedTargetGroup = {
            type: selectionTypes.TARGET_GROUP,
            item: group
          }
        }
      }
    }

    if (overrides) {
      if ((overrides.type === selectionTypes.NEW_TARGET_GROUP || overrides.type === selectionTypes.NEW_ITEM)) {
        uiStore.isPOIMenuExpanded = false
      } else if (overrides.type === selectionTypes.NONE) {
        uiStore.isPOIMenuExpanded = false
      }
    }

    this.set(_.merge(selection, overrides))
  }

  @action unselect () {
    uiStore.showIncomeTooltip = false
    if (!this._isEditing) {
      this._editableShape = undefined
    }
    errorStore.errorMessage = null
  }

  @action setEditControl (control) {
    this._editControl = control
  }

  @action setBufferForEditableShape (buffer) {
    if (this._editableShape) {
      this._editableShape.buffer = buffer
    }
  }

  @action setSelectedMenuItem (item) {
    this._selectedMenuItems = item ? [item] : []
  }

  @action setSelectedMenuItemGroup (group) {
    this._selectedMenuItems = group ? [group] : []
  }

  @action addSelectedMenuItem (item) {
    if (!item) return
    const itemId = item.id !== undefined && item.id !== null
      ? item.id
      : (item.inner_id !== undefined && item.inner_id !== null
          ? item.inner_id
          : (item.remote_db_id !== undefined && item.remote_db_id !== null
              ? item.remote_db_id
              : item.pk))
    if (itemId !== undefined && this.selectedMenuItemIds.some(id => String(id) === String(itemId))) return

    this._selectedMenuItems = this._selectedMenuItems.filter(menuItem => {
      if (menuItem instanceof TargetGroup || (menuItem && menuItem.pk != null && !menuItem.targetGroupId)) {
        return menuItem.pk !== item.targetGroupId
      }
      return true
    })

    this._selectedMenuItems.push(item)
  }

  @action addSelectedMenuItemGroup (group) {
    if (!group) return
    const groupPk = group.pk != null ? group.pk : group.id
    if (groupPk != null && this.selectedMenuItemIds.some(id => String(id) === String(groupPk))) return

    this._selectedMenuItems = this._selectedMenuItems.filter(
      (item) => item && item.targetGroupId !== groupPk
    )

    this._selectedMenuItems.push(group)
  }

  @computed get selectedItemsHasGroup () {
    return this.selectedMenuItems.some(item => item instanceof TargetGroup)
  }

  @computed get selectedMenuItemIds () {
    return this._selectedMenuItems.map(item => {
      if (!item) return undefined
      if (item.id !== undefined && item.id !== null) return item.id
      if (item.pk !== undefined && item.pk !== null) return item.pk
      if (item.inner_id !== undefined && item.inner_id !== null) return item.inner_id
      if (item.remote_db_id !== undefined && item.remote_db_id !== null) return item.remote_db_id
      return undefined
    }).filter(id => id !== undefined)
  }

  @computed get selectedGeopolygonIds () {
    return this._selectedMenuItems.map(item => item instanceof Geopolygon ? item.id : undefined).filter(id => id !== undefined)
  }

  @computed get selectedMarkerIds () {
    return this._selectedMenuItems.map(item => item instanceof SavedMarker ? item.id : undefined).filter(id => id !== undefined)
  }

  @computed get selectedBoundaryIds () {
    return this._selectedMenuItems.map(item => item instanceof Boundary && !item.isZipCode ? item.inner_id : undefined).filter(id => id !== undefined)
  }

  @computed get selectedZipCodeBoundaryIds () {
    return this._selectedMenuItems.map(item => item instanceof Boundary && item.isZipCode ? item.inner_id : undefined).filter(id => id !== undefined)
  }

  @computed get selectedTargetGroupIds () {
    return this._selectedMenuItems.map(item => item instanceof TargetGroup ? item.pk : undefined).filter(id => id !== undefined)
  }

  @computed get selectedMenuItemNames () {
    return this.hasMultipleSelectedMenuItems
      ? this._selectedMenuItems.map(item => Object.prototype.hasOwnProperty.call(item, 'name') || item.name !== undefined ? item.name : '')
      : []
  }

  @computed get hasMultipleSelectedMenuItems () {
    return this._selectedMenuItems.length > 1
  }

  @action clearSelectedMenuItems () {
    this._selectedMenuItems = []
  }

  @action enableEdit (shape, group) {
    uiStore.showIncomeTooltip = false
    const targetShape = shape || (this._menuItem && this._menuItem.item) || this.editableShape || this.item
    const targetGroup = group || (this._menuItem && this._menuItem.data && this._menuItem.data.group) || (this.data && this.data.group)
    if (!targetShape) return
    this._editableShape = targetShape
    this._isEditing = true
    if (targetShape.position && targetShape.position.latitude !== undefined) {
      this._originalMarkerPosition = {
        latitude: targetShape.position.latitude,
        longitude: targetShape.position.longitude
      }
    }
    // Navigate to item when editing it
    uiStore.updateBoundsOptionsHelper()
    uiStore.setBoundsToItem(targetShape)
    this.select(targetShape, { data: { group: targetGroup } }, true, true)
  }

  @computed get colorEditItem () {
    return (this._menuItem && this._menuItem.item) || this.item
  }

  @action selectColorEdit (e, showTransparencyOption) {
    if (e && e.stopPropagation) {
      e.stopPropagation()
    }
    const itemToEdit = this.colorEditItem
    if (!itemToEdit) return

    this._originalColorId = itemToEdit.color_id !== undefined ? itemToEdit.color_id : null
    this._originalTransparency = itemToEdit.transparency !== undefined ? itemToEdit.transparency : 50
    this.selectedColor = this._originalColorId

    // Snapshot child states for target groups
    this._originalChildStates = []
    if (itemToEdit.geopolygons) {
      itemToEdit.geopolygons.forEach(p => {
        this._originalChildStates.push({ item: p, color_id: p.color_id, transparency: p.transparency })
      })
    }
    if (itemToEdit.boundaries) {
      itemToEdit.boundaries.forEach(b => {
        this._originalChildStates.push({ item: b, color_id: b.color_id, transparency: b.transparency })
      })
    }
    if (itemToEdit.zipCodeBoundaries) {
      itemToEdit.zipCodeBoundaries.forEach(z => {
        this._originalChildStates.push({ item: z, color_id: z.color_id, transparency: z.transparency })
      })
    }
    if (itemToEdit.savedMarkers) {
      itemToEdit.savedMarkers.forEach(m => {
        this._originalChildStates.push({ item: m, color_id: m.color_id })
      })
    }

    uiStore.showColorMenuTransparency = showTransparencyOption

    uiStore.openClickTooltip({
      style: {
        width: 330
      },
      position: 'right-start',
      selector: '.tools.popup-menu',
      text: React.createElement(ColorPane)
    })
  }

  @action resetToOriginalColor () {
    const itemToEdit = this.colorEditItem
    const origColor = this._originalColorId !== undefined ? this._originalColorId : null
    const origTransparency = this._originalTransparency !== undefined && this._originalTransparency !== null ? this._originalTransparency : 50

    this.selectedColor = origColor
    this.selectedTransparency = origTransparency

    if (itemToEdit) {
      itemToEdit.color_id = origColor
      itemToEdit.transparency = origTransparency

      if (this._originalChildStates && this._originalChildStates.length > 0) {
        this._originalChildStates.forEach(child => {
          if (child.item) {
            child.item.color_id = child.color_id !== undefined ? child.color_id : null
            if (child.transparency !== undefined && child.transparency !== null) {
              child.item.transparency = child.transparency
            }
          }
        })
      }
    }
  }

  @action unselectColorEdit () {
    const itemToEdit = this.colorEditItem
    if (itemToEdit) {
      // Restore original color and transparency
      itemToEdit.color_id = this._originalColorId !== undefined ? this._originalColorId : null
      if (this._originalTransparency !== undefined && this._originalTransparency !== null) {
        itemToEdit.transparency = this._originalTransparency
      }

      // Restore child states if any
      if (this._originalChildStates && this._originalChildStates.length > 0) {
        this._originalChildStates.forEach(child => {
          if (child.item) {
            child.item.color_id = child.color_id !== undefined ? child.color_id : null
            if (child.transparency !== undefined && child.transparency !== null) {
              child.item.transparency = child.transparency
            }
          }
        })
      }
    }
    this._originalColorId = null
    this._originalTransparency = null
    this._originalChildStates = []

    this._isEditing = false
    this.selectedColor = null
    this.hidePopupMenu()
  }

  @action disableEdit () {
    this.editControl && this.editControl.disable()
    this._isEditing = false
  }

  @action cancelEdit () {
    if (this.editControl && typeof this.editControl.revertLayers === 'function') {
      this.editControl.revertLayers()
    }
    if (this._editableShape && this._originalMarkerPosition && this._editableShape.position) {
      this._editableShape.position.latitude = this._originalMarkerPosition.latitude
      this._editableShape.position.longitude = this._originalMarkerPosition.longitude
    }
    this._originalMarkerPosition = null
    this.disableEdit()
    this.select(this.item, { data: { group: this.data.group } }, false, true)
  }

  @action async saveEdit () {
    const editControl = this.editControl
    let updatedGeoJSON = null

    if (editControl) {
      const extractGeoJSON = (group) => {
        if (group && typeof group.eachLayer === 'function') {
          group.eachLayer(layer => {
            if (layer && typeof layer.toGeoJSON === 'function' && !updatedGeoJSON) {
              updatedGeoJSON = layer.toGeoJSON()
            }
          })
        }
      }

      extractGeoJSON(editControl._shapeGroup)
      if (!updatedGeoJSON) {
        extractGeoJSON(editControl._featureGroup)
      }

      if (typeof editControl.save === 'function') {
        editControl.save()
      }
      if (typeof editControl.disable === 'function') {
        editControl.disable()
      }
    }

    const shape = this.editableShape
    const group = (this.data && this.data.group) ||
      (shape && shape.targetGroupId && dataStore.targetGroups && dataStore.targetGroups.find(g => String(g.pk) === String(shape.targetGroupId)))

    if (updatedGeoJSON && shape && group) {
      await uiStore.updateGeopolygon(group, shape, updatedGeoJSON)
    } else if (shape && shape.updatePosition) {
      await shape.updatePosition()
    } else if (shape && shape.position && shape.id) {
      await api.patchMarker(shape.id, {
        latitude: shape.position.latitude,
        longitude: shape.position.longitude
      })
    }

    this._originalMarkerPosition = null
    this._isEditing = false
  }

  @action resetAllInteractions () {
    this.selectedColor = null
    if (this._isEditing) {
      this._isEditing = false
      this._editableShape = undefined
      if (this.editControl) this.editControl.disable()
    }

    // Stop any ongoing API calls so the UI doesn't hang spinning
    this.getNewCancelSource()
    uiStore.setProcessing(false)

    // Close any active rename forms
    if (dataStore && dataStore.targetGroups) {
      dataStore.targetGroups.forEach(group => {
        if (group.renameForm && group.renameForm.active) group.endRename()
        if (group.geopolygons) group.geopolygons.forEach(p => { if (p.renameForm && p.renameForm.active) p.endRename() })
        if (group.savedMarkers) group.savedMarkers.forEach(m => { if (m.renameForm && m.renameForm.active) m.endRename() })
        if (group.boundaries) group.boundaries.forEach(b => { if (b.renameForm && b.renameForm.active) b.endRename() })
        if (group.zipCodeBoundaries) group.zipCodeBoundaries.forEach(z => { if (z.renameForm && z.renameForm.active) z.endRename() })
      })
    }
  }

  @action updatePopupMenuPosition (y, x) {
    if (this._menuItem) {
      this._menuItem.y = y
      this._menuItem.x = x
    }
  }

  isItemMenuOpen (item) {
    if (!this._menuItem || !this._menuItem.visible || !this._menuItem.item) return false
    if (!item) return false
    if (this._menuItem.item === item) return true
    if (this._menuItem.item.pk != null && item.pk != null && String(this._menuItem.item.pk) === String(item.pk)) return true
    if (this._menuItem.item.id != null && item.id != null && String(this._menuItem.item.id) === String(item.id)) return true
    if (this._menuItem.item.name && item.name && this._menuItem.item.name === item.name) return true
    return false
  }

  @action showPopupMenu (item, overrides, ev) {
    if (this.isItemMenuOpen(item)) {
      this.hidePopupMenu()
      return
    }
    this.resetAllInteractions()
    this.hideSubTools()
    if (uiStore) {
      if (typeof uiStore.closeAllHeaderPopups === 'function') {
        uiStore.closeAllHeaderPopups()
      }
      if (typeof uiStore.clearHoveredTargetGroup === 'function') {
        uiStore.clearHoveredTargetGroup()
      }
      uiStore.hideClickTooltip()
      uiStore.hideDeletePrompt()
      uiStore.hideFilterPopup()
      uiStore.hideSortPopup()
      uiStore.hideLayerSettings()
      uiStore.hideHeatmapSettings()
      uiStore.hideChooseAddType()
      uiStore.closeToolbarMenu()
    }
    const isSelected = this.item && item ? item.name === this.item.name : false
    const el = (ev && (ev.currentTarget || ev.target)) || null
    const targetEl = (el && el.closest && (el.closest('.three') || el.closest('button') || el.closest('.target-group__dots'))) || el
    const target = targetEl && typeof targetEl.getBoundingClientRect === 'function'
      ? targetEl.getBoundingClientRect()
      : (ev && ev.target && typeof ev.target.getBoundingClientRect === 'function' ? ev.target.getBoundingClientRect() : { right: 300, top: 100, bottom: 100 })

    const x = target.right
    const y = target.top

    const selection = this._resolveSelection(item, overrides) || { item }

    this._menuItem = {
      ...selection,
      visible: true,
      x,
      y,
      isSelected
    }
  }

  @action showContextMenu (item, overrides, ev) {
    if (ev && ev.type !== 'contextmenu' && this.isItemMenuOpen(item)) {
      this.hidePopupMenu()
      return
    }
    this.resetAllInteractions()
    this.hideSubTools()
    if (uiStore) {
      if (typeof uiStore.closeAllHeaderPopups === 'function') {
        uiStore.closeAllHeaderPopups()
      }
      if (typeof uiStore.clearHoveredTargetGroup === 'function') {
        uiStore.clearHoveredTargetGroup()
      }
      uiStore.hideClickTooltip()
      uiStore.hideDeletePrompt()
      uiStore.hideFilterPopup()
      uiStore.hideSortPopup()
      uiStore.hideLayerSettings()
      uiStore.hideHeatmapSettings()
      uiStore.hideChooseAddType()
      uiStore.closeToolbarMenu()
    }
    const isSelected = this.item && item ? item.name === this.item.name : false

    const x = ev.clientX
    const y = ev.clientY

    const selection = this._resolveSelection(item, overrides) || { item }

    this._menuItem = {
      ...selection,
      visible: true,
      x,
      y,
      isSelected
    }
  }

  @action hidePopupMenu () {
    this._menuItem = null
    if (uiStore) {
      uiStore.hideClickTooltip()
      uiStore.hideDeletePrompt()
    }
  }

  @action toggleSubTools () {
    this._showSubTools = true
  }

  @action hideSubTools () {
    this._showSubTools = false
  }

  // for shape-drag functionality
  // @action enableMoving () {
  //   this._isMoving = true
  // }

  // for shape-drag functionality
  // @action disableMoving () {
  //   this._isMoving = false
  // }

  clear () {
    uiStore.showIncomeTooltip = false
    this.disableEdit()
    if (uiStore && typeof uiStore.restorePreviousSelectionBeforeAdd === 'function' && uiStore.previousSelectionBeforeAdd) {
      uiStore.restorePreviousSelectionBeforeAdd()
    } else {
      this.select(null, { type: selectionTypes.NONE })
      this.hideSubTools()
    }
  }

  @computed get focusedInteraction () {
    return this._data && this._data.focusedInteraction
  }

  passEvent (eventName, e) {
    if (this.item && this.item[eventName]) {
      this.item[eventName](e)
    }
  }
}

export default SelectionStore
